// ABOUTME: Modal that starts a packaging material usage export for one country and period.
// ABOUTME: Collects month, quarter or year plus the destination country, then hands it to the worker.

import { Box, useTheme } from "@mui/material"
import { useMemo, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import { countriesForDropdown } from "../../../utils/countries";
import { useExportPackagingUsageMutation } from "../../../newapi/order/shipmentsSlice";

const PERIOD_TYPES = ["month", "quarter", "year"];

// A report is always about a period that has ended, so the previous month is what the
// customer reaches for. In January that is December of the year before.
const previousMonth = () => {
    const now = new Date();
    const month = now.getMonth();
    return month === 0
        ? { year: now.getFullYear() - 1, period: 12 }
        : { year: now.getFullYear(), period: month };
};

const MExportPackagingUsage = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [exportPackagingUsage, { isLoading }] = useExportPackagingUsageMutation();

    const [periodType, setPeriodType] = useState("month");
    const [selection, setSelection] = useState(previousMonth);
    const [country, setCountry] = useState("");

    const countries = useMemo(() => countriesForDropdown(i18n.language), [i18n.language]);

    const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, index) => {
        const monthName = new Date(2000, index, 1).toLocaleDateString(i18n.language, { month: 'long' });
        return {
            id: index + 1,
            name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
            value: index + 1
        };
    }), [i18n.language]);

    const quarterOptions = useMemo(() => Array.from({ length: 4 }, (_, index) => ({
        id: index + 1,
        name: `Q${index + 1}`,
        value: index + 1
    })), []);

    // Five years back is as far as a packaging act report ever reaches.
    const yearOptions = useMemo(() => {
        const thisYear = new Date().getFullYear();
        return Array.from({ length: 6 }, (_, index) => ({
            id: thisYear - index,
            name: `${thisYear - index}`,
            value: thisYear - index
        }));
    }, []);

    const periodOptions = periodType === "quarter" ? quarterOptions : monthOptions;

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleClose = () => {
        setPeriodType("month");
        setSelection(previousMonth());
        setCountry("");
        props.handleClose();
    };

    // A year has no period of its own to pick, so switching to it drops the month.
    const handlePeriodTypeChange = (key, value) => {
        setPeriodType(value);
        if (value === "month") {
            setSelection((current) => ({ ...current, period: current.period > 12 ? 12 : current.period || 1 }));
        } else if (value === "quarter") {
            setSelection((current) => ({ ...current, period: Math.min(Math.ceil((current.period || 1) / 3), 4) }));
        }
    };

    const startExport = async () => {
        if (isLoading) return;

        const exportPromise = exportPackagingUsage({
            country,
            periodType,
            year: selection.year,
            period: periodType === "year" ? null : selection.period
        }).unwrap();

        toast.promise(exportPromise, {
            pending: t('export_processing'),
            success: t('export_success'),
            error: t('export_failed')
        });

        try {
            await exportPromise;
            handleClose();
        } catch (e) {
            // The toast above already told the user; the modal stays open to retry.
        }
    };

    let bodyBackground = colors.grey[0];
    if (theme.palette.mode === "dark") {
        bodyBackground = colors.grey[200];
    }

    const labelWidth = 140;

    const row = (label, control) => (
        <Box
            sx={{
                width:1,
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                gap:7
            }}
        >
            <Box sx={{ width: labelWidth, flexShrink: 0 }}>
                <Text light>{label}</Text>
            </Box>
            {control}
        </Box>
    );

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("export_packaging_usage")}</Subheading>
                <Text light>{t("export_packaging_usage_subtitle")}</Text>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background:bodyBackground,
                    padding:3,
                    pb:2,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2
                }}
            >
                {row(t("period_type"), (
                    <ShValidatedInputDropdown
                        name="periodType"
                        displayName="name"
                        changeField="value"
                        selected={PERIOD_TYPES.findIndex(item => item === periodType)}
                        options={PERIOD_TYPES.map((item, index) => ({ id: index, name: t(item), value: item }))}
                        noSelection={t("choose_option")}
                        onChange={handlePeriodTypeChange}
                    />
                ))}

                {periodType !== "year" && row(t(periodType), (
                    <ShValidatedInputDropdown
                        name="period"
                        displayName="name"
                        changeField="value"
                        selected={periodOptions.findIndex(item => item.value === selection.period)}
                        options={periodOptions}
                        noSelection={t("choose_option")}
                        onChange={(key, value) => setSelection((current) => ({ ...current, period: value }))}
                    />
                ))}

                {row(t("year"), (
                    <ShValidatedInputDropdown
                        name="year"
                        displayName="name"
                        changeField="value"
                        selected={yearOptions.findIndex(item => item.value === selection.year)}
                        options={yearOptions}
                        noSelection={t("choose_option")}
                        onChange={(key, value) => setSelection((current) => ({ ...current, year: value }))}
                    />
                ))}

                {row(t("country"), (
                    <ShValidatedInputDropdown
                        name="country"
                        displayName="name"
                        changeField="value"
                        selected={countries.findIndex(item => item.value === country)}
                        options={countries}
                        noSelection={t("choose_country")}
                        onChange={(key, value) => setCountry(value)}
                        searchOptions={{
                            enabled:true,
                            placeholder:t("search")
                        }}
                    />
                ))}
            </Box>
            <Box
                component="footer"
                sx={{
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:bodyBackground,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        isLoading={isLoading}
                        disabled={!country}
                        className={"group-btn"}
                        color={"green"}
                        block
                        variant="contained"
                        endIcon={<DownloadOutlinedIcon/>}
                        onClick={startExport}
                    >{t("export")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MExportPackagingUsage;
